import React, { useState } from 'react';
import {
  Box,
  Button,
  TextField,
  Typography,
  Checkbox,
  FormControlLabel,
  Link,
  InputAdornment,
  IconButton,
  Alert,
  CircularProgress,
  Select,
  MenuItem,
  FormControl,
  Autocomplete,
} from '@mui/material';
import { Visibility, VisibilityOff, ArrowBack } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { Country, State } from 'country-state-city';
import Logo from '../../assets/Logo.png';
import AuthImage from '../../assets/auth.img.png';
import GoogleAsset from '../../assets/icons8-google 1.png';
import AzureAsset from '../../assets/Frame 59.png';

const allCountries = Country.getAllCountries();

interface CountryOption {
  name: string;
  isoCode: string;
  flag: string;
}

interface StateOption {
  name: string;
  isoCode: string;
}

interface FormData {
  username: string;
  email: string;
  password: string;
  confirm_password: string;
  company_name: string;
  industry_type: string;
  company_registration_number: string;
  company_address: string;
  country: string;
  state_province: string;
  phone_number: string;
  website: string;
}

const INDUSTRY_OPTIONS = [
  { value: 'oil_and_gas', label: 'Oil & Gas' },
  { value: 'mining', label: 'Mining' },
  { value: 'transportation', label: 'Transportation' },
  { value: 'logistics', label: 'Logistics' },
  { value: 'manufacturing', label: 'Manufacturing' },
  { value: 'utilities', label: 'Utilities' },
  { value: 'other', label: 'Other' },
];

const SignupPage: React.FC = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState<FormData>({
    username: '',
    email: '',
    password: '',
    confirm_password: '',
    company_name: '',
    industry_type: 'oil_and_gas',
    company_registration_number: '',
    company_address: '',
    country: '',
    state_province: '',
    phone_number: '',
    website: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [agreed, setAgreed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedCountry, setSelectedCountry] = useState<CountryOption | null>(null);
  const [selectedState, setSelectedState] = useState<StateOption | null>(null);
  const [availableStates, setAvailableStates] = useState<StateOption[]>([]);

  const handleChange = (field: keyof FormData) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({ ...prev, [field]: e.target.value }));
    setError('');
  };

  const validateStep1 = () => {
    if (!formData.username) return 'Username is required';
    if (!formData.email) return 'Email is required';
    if (!/\S+@\S+\.\S+/.test(formData.email)) return 'Enter a valid email address';
    if (!formData.password) return 'Password is required';
    if (formData.password.length < 8) return 'Password must be at least 8 characters';
    if (formData.password !== formData.confirm_password) return 'Passwords do not match';
    return '';
  };

  const handleNext = () => {
    const err = validateStep1();
    if (err) { setError(err); return; }
    setError('');
    setStep(2);
  };

  const handleSubmit = async () => {
    if (!formData.company_name) { setError('Company name is required'); return; }
    if (!formData.country) { setError('Country is required'); return; }
    if (!agreed) { setError('You must agree to the Terms of Service and Privacy Policy'); return; }

    setLoading(true);
    setError('');

    try {
      const response = await fetch(
        'https://petrotag360api.itserviceafrica.com/api/v1/onboard/auth/register/',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(formData),
        }
      );
      const data = await response.json();
      if (response.ok) {
        navigate('/');
      } else {
        const msg = data?.detail || data?.message ||
          Object.values(data).flat().join(' ') ||
          'Registration failed. Please try again.';
        setError(String(msg));
      }
    } catch {
      setError('Network error. Please check your connection and try again.');
    }

    setLoading(false);
  };

  const inputSx = {
    '& .MuiOutlinedInput-root': {
      borderRadius: '10px',
      backgroundColor: '#FFFFFF',
      fontSize: '0.85rem',
      height: '43px',
      '& fieldset': { borderColor: 'rgba(217, 217, 217, 1)', borderWidth: '1px' },
      '&:hover fieldset': { borderColor: 'rgba(217, 217, 217, 1)', borderWidth: '1px' },
      '&.Mui-focused fieldset': { borderColor: 'rgba(217, 217, 217, 1) !important', borderWidth: '1px !important' },
      '& input': { padding: '10px 10px' },
      '& input:-webkit-autofill, & input:-webkit-autofill:hover, & input:-webkit-autofill:focus': {
        WebkitBoxShadow: '0 0 0 100px #ffffff inset',
        WebkitTextFillColor: '#000000',
        caretColor: '#000000',
      },
    },
  };

  const selectSx = {
    borderRadius: '10px',
    backgroundColor: '#FFFFFF',
    fontSize: '0.85rem',
    height: '43px',
    '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(217, 217, 217, 1)', borderWidth: '1px' },
    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(217, 217, 217, 1)', borderWidth: '1px' },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(217, 217, 217, 1) !important', borderWidth: '1px !important' },
  };

  const labelSx = {
    fontFamily: '"Montserrat", sans-serif',
    fontWeight: 500,
    fontSize: '14px',
    lineHeight: '100%',
    letterSpacing: '0%',
    color: 'rgba(0, 0, 0, 1)',
    mb: 0.5,
  };

  const colGap = 1.5;

  return (
    <Box sx={{ display: 'flex', height: '100vh', overflow: 'hidden' }}>

      {/* ── LEFT PANEL ─────────────────────────────────────────────── */}
      <Box
        sx={{
          width: { xs: '100%', md: '45%' },
          display: 'flex',
          flexDirection: 'column',
          px: { xs: 4, sm: 6, md: 7 },
          pt: { xs: 3, md: 3.5 },
          pb: { xs: 2, md: 2 },
          backgroundColor: '#ffffff',
          height: '100%',
          overflow: 'hidden',
        }}
      >
        {/* Logo */}
        <Box sx={{ mb: 2 }}>
          <Box
            component="img"
            src={Logo}
            alt="PetroTag360"
            sx={{ height: 80, objectFit: 'contain', objectPosition: 'left' }}
          />
        </Box>

        {/* ── STEP 1: Account Details ─────────────────────────────── */}
        {step === 1 && (
          <>
            <Box sx={{ mb: 2 }}>
              <Typography
                sx={{
                  fontFamily: '"Unbounded", sans-serif',
                  fontWeight: 400,
                  fontSize: '22px',
                  lineHeight: '48px',
                  color: 'rgba(24, 28, 54, 1)',
                }}
              >
                Welcome to
              </Typography>
              <Typography
                sx={{
                  fontFamily: '"Unbounded", sans-serif',
                  fontWeight: 700,
                  fontSize: '33.31px',
                  lineHeight: '48px',
                  color: 'rgba(0, 0, 0, 1)',
                  opacity: 0.8,
                  mt: -0.5,
                }}
              >
                PetroTag360!
              </Typography>
              <Typography
                sx={{
                  fontFamily: '"Montserrat", sans-serif',
                  fontWeight: 400,
                  fontSize: '14px',
                  lineHeight: '100%',
                  color: 'rgba(87, 81, 81, 1)',
                  mt: 0.5,
                }}
              >
                Fuel tracking made transparent, smart, and reliable
              </Typography>
            </Box>

            {error && <Alert severity="error" sx={{ mb: 1.5, py: 0 }}>{error}</Alert>}

            <Box component="form" onSubmit={(e) => { e.preventDefault(); handleNext(); }}>

              {/* Username + Email row */}
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: colGap, mb: 1.5 }}>
                <Box>
                  <Typography sx={labelSx}>Username</Typography>
                  <TextField fullWidth placeholder="Username" size="small"
                    value={formData.username} onChange={handleChange('username')} sx={inputSx} />
                </Box>
                <Box>
                  <Typography sx={labelSx}>Email address</Typography>
                  <TextField fullWidth placeholder="Enter your email" type="email" size="small"
                    value={formData.email} onChange={handleChange('email')} sx={inputSx} />
                </Box>
              </Box>

              {/* Password + Confirm row */}
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: colGap, mb: 2 }}>
                <Box>
                  <Typography sx={labelSx}>Password</Typography>
                  <TextField
                    fullWidth placeholder="Password" size="small"
                    type={showPassword ? 'text' : 'password'}
                    value={formData.password} onChange={handleChange('password')} sx={inputSx}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton size="small" onClick={() => setShowPassword(!showPassword)} edge="end">
                            {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </Box>
                <Box>
                  <Typography sx={labelSx}>Confirm Password</Typography>
                  <TextField
                    fullWidth placeholder="Confirm password" size="small"
                    type={showConfirm ? 'text' : 'password'}
                    value={formData.confirm_password} onChange={handleChange('confirm_password')} sx={inputSx}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton size="small" onClick={() => setShowConfirm(!showConfirm)} edge="end">
                            {showConfirm ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </Box>
              </Box>

              {/* Next button */}
              <Button
                fullWidth type="submit" variant="contained"
                sx={{
                  height: '43px',
                  backgroundColor: 'rgba(43, 103, 176, 1)',
                  borderRadius: '10px',
                  fontSize: '0.95rem',
                  fontWeight: 700,
                  textTransform: 'none',
                  boxShadow: 'none',
                  mb: 1.5,
                  '&:hover': { backgroundColor: 'rgba(33, 85, 150, 1)', boxShadow: 'none' },
                }}
              >
                Next →
              </Button>

              {/* Social signup */}
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: colGap, mb: 1.5 }}>
                <Button variant="outlined"
                  startIcon={<Box component="img" src={GoogleAsset} alt="Google" sx={{ width: 18, height: 18 }} />}
                  sx={{
                    height: '43px', borderColor: '#E5E7EB', color: '#374151',
                    borderRadius: '10px', textTransform: 'none', fontWeight: 500,
                    fontSize: '0.82rem', backgroundColor: '#fff', boxShadow: 'none',
                    '&:hover': { borderColor: '#D1D5DB', backgroundColor: '#F9FAFB', boxShadow: 'none' },
                  }}
                >
                  Sign up with Google
                </Button>
                <Button variant="outlined"
                  startIcon={<Box component="img" src={AzureAsset} alt="Azure" sx={{ width: 18, height: 18 }} />}
                  sx={{
                    height: '43px', borderColor: '#E5E7EB', color: '#374151',
                    borderRadius: '10px', textTransform: 'none', fontWeight: 500,
                    fontSize: '0.82rem', backgroundColor: '#fff', boxShadow: 'none',
                    '&:hover': { borderColor: '#D1D5DB', backgroundColor: '#F9FAFB', boxShadow: 'none' },
                  }}
                >
                  Sign up with Azure
                </Button>
              </Box>

              {/* Login link */}
              <Box sx={{ textAlign: 'center' }}>
                <Typography sx={{ fontFamily: '"Montserrat", sans-serif', fontWeight: 500, fontSize: '14px', lineHeight: '100%', letterSpacing: '0%', color: '#6B7280' }}>
                  Have an account already?{' '}
                  <Link component="button" type="button" onClick={() => navigate('/')} underline="hover"
                    sx={{ fontFamily: '"Montserrat", sans-serif', fontWeight: 500, fontSize: '14px', lineHeight: '100%', letterSpacing: '0%', color: 'rgba(43, 103, 176, 1)', verticalAlign: 'baseline' }}>
                    Log in
                  </Link>
                </Typography>
              </Box>
            </Box>
          </>
        )}

        {/* ── STEP 2: Company Details ──────────────────────────────── */}
        {step === 2 && (
          <>
            <Box sx={{ mb: 2 }}>
              <Typography
                sx={{
                  fontFamily: '"Unbounded", sans-serif',
                  fontWeight: 700,
                  fontSize: '26px',
                  lineHeight: '40px',
                  color: 'rgba(0, 0, 0, 1)',
                  opacity: 0.8,
                }}
              >
                Company Details
              </Typography>
              <Typography
                sx={{
                  fontFamily: '"Montserrat", sans-serif',
                  fontWeight: 400,
                  fontSize: '13px',
                  lineHeight: '100%',
                  color: 'rgba(87, 81, 81, 1)',
                  mt: 0.5,
                }}
              >
                Tell us about your organisation
              </Typography>
            </Box>

            {error && <Alert severity="error" sx={{ mb: 1.5, py: 0 }}>{error}</Alert>}

            <Box component="form" onSubmit={(e) => { e.preventDefault(); handleSubmit(); }}>

              {/* Company Name + Industry */}
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: colGap, mb: 1.5 }}>
                <Box>
                  <Typography sx={labelSx}>Company Name</Typography>
                  <TextField fullWidth placeholder="Company name" size="small"
                    value={formData.company_name} onChange={handleChange('company_name')} sx={inputSx} />
                </Box>
                <Box>
                  <Typography sx={labelSx}>Industry Type</Typography>
                  <FormControl fullWidth size="small">
                    <Select
                      value={formData.industry_type}
                      onChange={(e) => { setFormData(prev => ({ ...prev, industry_type: e.target.value })); setError(''); }}
                      sx={selectSx}
                    >
                      {INDUSTRY_OPTIONS.map(opt => (
                        <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
              </Box>

              {/* Reg Number + Phone */}
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: colGap, mb: 1.5 }}>
                <Box>
                  <Typography sx={labelSx}>Reg. Number</Typography>
                  <TextField fullWidth placeholder="Registration number" size="small"
                    value={formData.company_registration_number} onChange={handleChange('company_registration_number')} sx={inputSx} />
                </Box>
                <Box>
                  <Typography sx={labelSx}>Phone Number</Typography>
                  <TextField fullWidth placeholder="+234 000 000 0000" size="small"
                    value={formData.phone_number} onChange={handleChange('phone_number')} sx={inputSx} />
                </Box>
              </Box>

              {/* Country + State */}
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: colGap, mb: 1.5 }}>
                <Box>
                  <Typography sx={labelSx}>Country</Typography>
                  <Autocomplete
                    options={allCountries}
                    getOptionLabel={(option) => option.name}
                    value={selectedCountry}
                    onChange={(_, newValue) => {
                      setSelectedCountry(newValue);
                      setSelectedState(null);
                      if (newValue) {
                        const states = State.getStatesOfCountry(newValue.isoCode).map(s => ({ name: s.name, isoCode: s.isoCode }));
                        setAvailableStates(states);
                        setFormData(prev => ({ ...prev, country: newValue.name, state_province: '' }));
                      } else {
                        setAvailableStates([]);
                        setFormData(prev => ({ ...prev, country: '', state_province: '' }));
                      }
                    }}
                    renderOption={(props, option) => (
                      <Box component="li" {...props} sx={{ fontSize: '0.85rem', gap: 1 }}>
                        <span style={{ fontSize: '1.1rem' }}>{option.flag}</span>
                        {option.name}
                      </Box>
                    )}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="Select country"
                        size="small"
                        sx={{
                          ...inputSx,
                          '& .MuiOutlinedInput-root': {
                            ...inputSx['& .MuiOutlinedInput-root'],
                            height: '43px',
                            flexWrap: 'nowrap',
                            '& input': { padding: '2px 10px' },
                          },
                        }}
                        InputProps={{
                          ...params.InputProps,
                          startAdornment: selectedCountry ? (
                            <InputAdornment position="start" sx={{ ml: 1 }}>
                              <span style={{ fontSize: '1.1rem' }}>{selectedCountry.flag}</span>
                            </InputAdornment>
                          ) : null,
                        }}
                      />
                    )}
                  />
                </Box>
                <Box>
                  <Typography sx={labelSx}>State / Province</Typography>
                  <Autocomplete
                    options={availableStates}
                    getOptionLabel={(option) => option.name}
                    value={selectedState}
                    disabled={availableStates.length === 0}
                    onChange={(_, newValue) => {
                      setSelectedState(newValue);
                      setFormData(prev => ({ ...prev, state_province: newValue?.name || '' }));
                    }}
                    renderOption={(props, option) => (
                      <Box component="li" {...props} sx={{ fontSize: '0.85rem' }}>
                        {option.name}
                      </Box>
                    )}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder={availableStates.length === 0 ? 'Select country first' : 'Select state'}
                        size="small"
                        sx={{
                          ...inputSx,
                          '& .MuiOutlinedInput-root': {
                            ...inputSx['& .MuiOutlinedInput-root'],
                            height: '43px',
                            flexWrap: 'nowrap',
                            '& input': { padding: '2px 10px' },
                          },
                        }}
                      />
                    )}
                  />
                </Box>
              </Box>

              {/* Address + Website */}
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: colGap, mb: 1.5 }}>
                <Box>
                  <Typography sx={labelSx}>Company Address</Typography>
                  <TextField fullWidth placeholder="Company address" size="small"
                    value={formData.company_address} onChange={handleChange('company_address')} sx={inputSx} />
                </Box>
                <Box>
                  <Typography sx={labelSx}>Website</Typography>
                  <TextField fullWidth placeholder="https://yoursite.com" size="small"
                    value={formData.website} onChange={handleChange('website')} sx={inputSx} />
                </Box>
              </Box>

              {/* Terms */}
              <FormControlLabel
                control={
                  <Checkbox size="small" checked={agreed} onChange={(e) => setAgreed(e.target.checked)}
                    sx={{ color: '#D1D5DB', '&.Mui-checked': { color: 'rgba(43, 103, 176, 1)' }, p: '6px' }} />
                }
                label={
                  <Typography sx={{ fontFamily: '"Montserrat", sans-serif', fontWeight: 500, fontSize: '12px', lineHeight: '100%', letterSpacing: '0%', color: '#374151' }}>
                    I agree to the{' '}
                    <Link href="#" sx={{ fontFamily: '"Montserrat", sans-serif', fontWeight: 500, fontSize: '12px', color: 'rgba(43, 103, 176, 1)', textDecoration: 'underline', textDecorationStyle: 'solid', textDecorationSkipInk: 'auto' }}>
                      Terms of Service
                    </Link>
                    {' '}and{' '}
                    <Link href="#" sx={{ fontFamily: '"Montserrat", sans-serif', fontWeight: 500, fontSize: '12px', color: 'rgba(43, 103, 176, 1)', textDecoration: 'underline', textDecorationStyle: 'solid', textDecorationSkipInk: 'auto' }}>
                      Privacy Policy
                    </Link>.
                  </Typography>
                }
                sx={{ mb: 1, alignItems: 'center', '& .MuiFormControlLabel-label': { pt: 0 } }}
              />

              {/* Buttons row */}
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: colGap }}>
                <Button variant="outlined" onClick={() => { setStep(1); setError(''); }}
                  startIcon={<ArrowBack fontSize="small" />}
                  sx={{
                    height: '43px', borderColor: 'rgba(217, 217, 217, 1)', color: 'rgba(0,0,0,0.7)',
                    borderRadius: '10px', textTransform: 'none', fontWeight: 600,
                    fontSize: '0.85rem', boxShadow: 'none',
                    '&:hover': { borderColor: '#C4C4C4', backgroundColor: '#F9FAFB', boxShadow: 'none' },
                  }}
                >
                  Back
                </Button>
                <Button fullWidth type="submit" variant="contained" disabled={loading}
                  sx={{
                    height: '43px',
                    backgroundColor: 'rgba(43, 103, 176, 1)',
                    borderRadius: '10px',
                    fontSize: '0.95rem',
                    fontWeight: 700,
                    textTransform: 'none',
                    boxShadow: 'none',
                    '&:hover': { backgroundColor: 'rgba(33, 85, 150, 1)', boxShadow: 'none' },
                    '&:disabled': { backgroundColor: '#93C5FD' },
                  }}
                >
                  {loading ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CircularProgress size={18} sx={{ color: '#fff' }} />
                      Creating account...
                    </Box>
                  ) : 'Create Account'}
                </Button>
              </Box>
            </Box>
          </>
        )}
      </Box>

      {/* ── RIGHT PANEL ────────────────────────────────────────────── */}
      <Box
        sx={{
          display: { xs: 'none', md: 'block' },
          width: '55%',
          height: '100%',
          backgroundColor: '#0A0F1E',
          overflow: 'hidden',
        }}
      >
        <Box
          component="img"
          src={AuthImage}
          alt="PetroTag360 dashboard preview"
          sx={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            objectPosition: 'center top',
          }}
        />
      </Box>

    </Box>
  );
};

export default SignupPage;
